import { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress,
  Tabs, Tab, Alert, Card, CardContent, Chip, Divider, Grid,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Psychology as AIIcon,
  TrendingDown as RiskIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface SoapNote { subjective: string; objective: string; assessment: string; plan: string }
interface ForecastItem {
  product_id: number; name: string; stock_quantity: number;
  reorder_level: number; avg_daily_usage: number; days_remaining: number; urgency: string;
}

const urgencyColor = (u: string) =>
  u === 'Critical' ? 'error' : u === 'High' ? 'warning' : u === 'Medium' ? 'info' : 'success';

export default function AIAssistPage() {
  const [tab, setTab] = useState(0);

  // SOAP state
  const [species, setSpecies] = useState('');
  const [ageYears, setAgeYears] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [complaint, setComplaint] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [soap, setSoap] = useState<SoapNote | null>(null);
  const [soapLoading, setSoapLoading] = useState(false);
  const [soapError, setSoapError] = useState('');

  // Inventory forecast state
  const [forecasts, setForecasts] = useState<ForecastItem[]>([]);
  const [forecastRec, setForecastRec] = useState('');
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState('');

  async function generateSoap() {
    if (!complaint.trim()) return;
    setSoapLoading(true); setSoapError(''); setSoap(null);
    try {
      const res = await api.post<{ soap: SoapNote }>(API_ENDPOINTS.AI.SOAP_ASSIST, {
        species: species || undefined,
        age_years: ageYears ? parseFloat(ageYears) : undefined,
        weight_kg: weightKg ? parseFloat(weightKg) : undefined,
        complaint,
        symptoms: symptoms || undefined,
      });
      setSoap(res.soap);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'AI request failed';
      setSoapError(msg);
    } finally {
      setSoapLoading(false);
    }
  }

  async function loadForecast() {
    setForecastLoading(true); setForecastError(''); setForecasts([]); setForecastRec('');
    try {
      const res = await api.get<{ forecasts: ForecastItem[]; recommendation: string }>(
        API_ENDPOINTS.AI.INVENTORY_FORECAST,
      );
      setForecasts(res.forecasts);
      setForecastRec(res.recommendation);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Forecast request failed';
      setForecastError(msg);
    } finally {
      setForecastLoading(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AIIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>AI Assistant</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="SOAP Note Generator" />
        <Tab label="Inventory Forecast" />
      </Tabs>

      {/* ── SOAP Note Generator ── */}
      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>SOAP Note Generator</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Generate a structured veterinary SOAP note. Only species, age, weight, complaint, and symptoms are sent — no client PII.
          </Typography>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Species</InputLabel>
                <Select value={species} label="Species" onChange={e => setSpecies(e.target.value)}>
                  <MenuItem value="">Unknown</MenuItem>
                  {['Dog', 'Cat', 'Rabbit', 'Bird', 'Hamster', 'Guinea Pig', 'Reptile', 'Other'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Age (years)" type="number"
                value={ageYears} onChange={e => setAgeYears(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Weight (kg)" type="number"
                value={weightKg} onChange={e => setWeightKg(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Chief Complaint *" required
                value={complaint} onChange={e => setComplaint(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reported Symptoms" multiline rows={2}
                value={symptoms} onChange={e => setSymptoms(e.target.value)} />
            </Grid>
          </Grid>

          <Button variant="contained" startIcon={soapLoading ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
            onClick={generateSoap} disabled={soapLoading || !complaint.trim()}>
            {soapLoading ? 'Generating…' : 'Generate SOAP Note'}
          </Button>

          {soapError && <Alert severity="error" sx={{ mt: 2 }}>{soapError}</Alert>}

          {soap && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              {(['subjective', 'objective', 'assessment', 'plan'] as const).map(key => (
                <Box key={key} mb={2}>
                  <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" color="primary" mb={0.5}>
                    {key}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{soap[key]}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* ── Inventory Forecast ── */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Inventory Forecast</Typography>
            <Button variant="contained" startIcon={forecastLoading ? <CircularProgress size={16} color="inherit" /> : <InventoryIcon />}
              onClick={loadForecast} disabled={forecastLoading}>
              {forecastLoading ? 'Loading…' : 'Run Forecast'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Calculates average daily usage from the last 60 days of dispense records and estimates days of stock remaining.
          </Typography>

          {forecastError && <Alert severity="error">{forecastError}</Alert>}

          {forecastRec && (
            <Alert severity="info" icon={<RiskIcon />} sx={{ mb: 2 }}>
              <strong>AI Recommendation:</strong> {forecastRec}
            </Alert>
          )}

          {forecasts.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {forecasts.map(f => (
                <Card key={f.product_id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body1" fontWeight={600}>{f.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Stock: {f.stock_quantity} | Reorder at: {f.reorder_level} | Avg daily use: {f.avg_daily_usage}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip size="small" color={urgencyColor(f.urgency) as 'error' | 'warning' | 'info' | 'success'}
                          label={f.urgency} />
                        <Typography variant="caption" display="block" mt={0.5}>
                          {f.days_remaining >= 999 ? 'No usage data' : `~${f.days_remaining} days left`}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
